import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryEdit from '../../modal/CategoryEdit';
import { Attendee, Catalog } from '../../types';
import { get } from '../../utils/api';
import CategoryRemove from './CategoryRemove';
import './Categories.css';
import { useAppSelector } from '../../hooks/store';
import CategoryModal from '../../modal/CategoryModal';

export interface CategoriesProps {
    attendee: Attendee;
};

type CategoriesResult = {
	categories: Catalog[]
};

const Categories: FC<CategoriesProps> = ({ attendee }) => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const categories = useAppSelector(store => store.categories);
    const quantity = i18n.language === 'en' ? 'quantity_en' : 'quantity_ru';
    const comments = i18n.language === 'en' ? 'comments_en' : 'comments_ru';
    const [ catalog, setCatalog ] = useState<Catalog[]>([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ isCategoryAddOpen, setIsCategoryAddOpen ] = useState(false);
    const [ update, setUpdate ] = useState(false);
    const [ canEdit, setCanEdit ] = useState(false);

    // Вычисляем можем ли еще редактировать профайл
    useEffect(() => {
        if(!user || !event) return;
        if(attendee.id !== user.id) return;
        if(!event.editing_until) {
            setCanEdit(true);
            return;
        }
        const today = Date.now() + 3 * 60 * 60 * 1000; // +3 часа
        const until = Date.parse(event.editing_until);
        if(today <= until) setCanEdit(true);
    }, [ user, event, attendee ]);

    // Запрашиваем список категорий участника
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError('');
            setCatalog([]);
            const result = await get('/catalog', { attendee_id: attendee.id });
            if(result.error) {
                setError(result.error.text[lang]);
            } 
            if(result.data) {
                let categories = (result.data as CategoriesResult).categories;
                categories.sort((a, b) => a[name].localeCompare(b[name]));
                setCatalog(categories);
            }
            setIsLoading(false);
        };
        fetchData();
        // ВНИМАНИЕ! Зависимость от update нужна для повторного запроса!
	}, [ attendee, lang, update, name ]);

    const getPriceSegment = (segment: boolean[]) => {
        const price_segment: string[] = [];
        if (segment[0]) price_segment.push(t('category.price_segment_economy'));
        if (segment[1]) price_segment.push(t('category.price_segment_medium'));
        if (segment[2]) price_segment.push(t('category.price_segment_premium'));
        if (price_segment.length > 0 && event?.use_price_segment) {
            return <><br/><span>{`${t('category.price_segment')}: ${price_segment.join(', ')}`}</span></>;
        } else {
            return null;
        }
    };

    if(isLoading) return <p>{t('common.loading')}</p>;
    if(error) return <p className='error'>{error}</p>;

    return (
        <div>
            <h2>{t('category.' + attendee.role + '.title')}</h2>
            {catalog.length > 0 ?
            <div className='Categories'>
                <div className='line'>
                    <div className='name'>{t('category.' + attendee.role + '.category')}</div>
                    <div className='quantity'>{t('category.' + attendee.role + '.quantity')}</div>
                </div>
                {catalog.map(cat => (
                    <div key={cat.category_id} className='line'>
                        <div className='name'>
                            <b>{cat[name]}</b>
                            {cat[comments] && <><br/><span>{cat[comments]}</span></>}
                            {getPriceSegment(cat.price_segment)}
                        </div>
                        <div className='quantity'>{cat[quantity]}</div>
                        {canEdit && <CategoryEdit
                            category={cat}
                            onSuccess={() => setUpdate(!update)}
                        />}
                        {canEdit && <CategoryRemove
                            attendee_id={attendee.id}
                            category_id={cat.category_id}
                            confirm={t('category.' + attendee.role + '.confirm')}
                            onSuccess={() => setUpdate(!update)}
                        />}
                    </div>
                ))}
                
            </div> : t('category.' + attendee.role + '.nocategories')}

            {(canEdit && catalog.length < categories.length) && <>
                <p><span className='Link' onClick={() => setIsCategoryAddOpen(true)}>
                    {t('category.' + attendee.role + '.submit')}
                </span></p>
                <CategoryModal
                    catalog={catalog}
                    isOpen={isCategoryAddOpen}
                    onClose={() => {
                        setUpdate(!update);
                        setIsCategoryAddOpen(false);
                    }}
                />
            </>}
        </div>
	);
};

export default Categories;
